import * as React from 'react';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { ExistingCheck } from '../actions/navigationWorker';

type Props = {
    link?: string;
    message?: string;
    linkTitle?: string;
    hide?: boolean
}

function SuccessMessage({ message, link, linkTitle }: Props) {
    const [isVisible, setIsVisible] = React.useState(false);
    const [batchResults, setBatchResults] = React.useState<any>(null);
    
    React.useEffect(() => {
        // Fade in animation
        setTimeout(() => setIsVisible(true), 100);
        
        // Check for batch filing results
        const batchResultsJson = sessionStorage.getItem('batch_filing_results');
        if (batchResultsJson) {
            try {
                setBatchResults(JSON.parse(batchResultsJson));
                sessionStorage.removeItem('batch_filing_results');
            } catch (e) {
                console.error('Error parsing batch results:', e);
            }
        }
    }, []);

    const back_to_start = () => {
        ExistingCheck();
    }

    const openInXplan = () => {
        if (link) {
            window.open(link, '_blank');
        }
    }

    const successIcon = (
        <div className="success-icon-wrapper" style={{
            display: 'inline-block',
            animation: isVisible ? 'scaleIn 0.5s ease-out' : 'none',
            transform: isVisible ? 'scale(1)' : 'scale(0)'
        }}>
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="40" cy="40" r="38" fill="#10B981" stroke="#059669" strokeWidth="4"/>
                <path d="M25 40L35 50L55 30" stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </div>
    );

    const containerStyle: React.CSSProperties = {
        position: 'fixed',
        top: '45px',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto'
    };

    const modernStyles: React.CSSProperties = {
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.5s ease-out',
        padding: '32px 24px',
        maxWidth: '500px',
        width: '100%'
    };

    const headingStyle: React.CSSProperties = {
        fontSize: '20px',
        fontWeight: 600,
        color: '#1a1a1a',
        marginTop: '24px',
        marginBottom: '8px',
        lineHeight: '1.4'
    };

    const messageStyle: React.CSSProperties = {
        fontSize: '14px',
        color: '#666',
        lineHeight: '1.6',
        marginBottom: '24px'
    };

    const timestampStyle: React.CSSProperties = {
        fontSize: '12px',
        color: '#999',
        marginBottom: '24px'
    };

    const buttonContainerStyle: React.CSSProperties = {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        marginTop: '24px',
        flexWrap: 'wrap'
    };

    const currentTime = new Date().toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric', 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
    });

    // Render batch filing results if available
    const renderBatchResults = () => {
        if (!batchResults) return null;
        
        return (
            <div style={{ marginTop: '16px', marginBottom: '16px', textAlign: 'left' }}>
                {batchResults.succeeded && batchResults.succeeded.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ 
                            fontWeight: 600, 
                            color: '#10B981', 
                            marginBottom: '8px',
                            fontSize: '14px'
                        }}>
                            ✓ Attached to note ({batchResults.succeeded.length}):
                        </div>
                        <ul style={{ 
                            margin: '0 0 0 20px', 
                            padding: 0, 
                            fontSize: '13px',
                            color: '#666'
                        }}>
                            {batchResults.succeeded.map((subject: string, idx: number) => (
                                <li key={idx} style={{ marginBottom: '4px' }}>{subject}</li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {batchResults.failed && batchResults.failed.length > 0 && (
                    <div>
                        <div style={{ 
                            fontWeight: 600, 
                            color: '#D13438', 
                            marginBottom: '8px',
                            fontSize: '14px'
                        }}>
                            ✗ Failed to attach ({batchResults.failed.length}):
                        </div>
                        <ul style={{ 
                            margin: '0 0 0 20px', 
                            padding: 0, 
                            fontSize: '13px',
                            color: '#666'
                        }}>
                            {batchResults.failed.map((item: any, idx: number) => (
                                <li key={idx} style={{ marginBottom: '4px' }}>
                                    {item.subject}
                                    <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                                        {item.error}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    if (link && message) {
        return (
            <div style={containerStyle}>
                <div style={modernStyles}>
                    <div style={{ textAlign: 'center' }}>
                        {successIcon}
                    <h2 style={headingStyle}>Successfully Filed!</h2>
                    <p style={messageStyle}>{message}</p>
                    {renderBatchResults()}
                    <p style={timestampStyle}>Filed on {currentTime}</p>
                    <div style={buttonContainerStyle}>
                        <PrimaryButton 
                            text={linkTitle || 'View in Xplan'} 
                            onClick={openInXplan}
                            styles={{
                                root: { 
                                    minWidth: '140px', 
                                    height: '40px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    backgroundColor: '#5D3FD6',
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: '#4A2FC7'
                                },
                                rootPressed: {
                                    backgroundColor: '#3B24A8'
                                }
                            }}
                        />
                        <DefaultButton 
                            text='File Another' 
                            onClick={back_to_start}
                            styles={{
                                root: { 
                                    minWidth: '140px', 
                                    height: '40px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    backgroundColor: '#ffffff',
                                    color: '#5D3FD6',
                                    border: '2px solid #5D3FD6'
                                },
                                rootHovered: {
                                    backgroundColor: '#f8f6fc',
                                    color: '#4A2FC7',
                                    border: '2px solid #4A2FC7'
                                },
                                rootPressed: {
                                    backgroundColor: '#ede8f7',
                                    color: '#3B24A8',
                                    border: '2px solid #3B24A8'
                                }
                            }}
                        />
                    </div>
                </div>
                </div>
            </div>
        );
    }

    if (message) {
        return (
            <div style={containerStyle}>
                <div style={modernStyles}>
                    <div style={{ textAlign: 'center' }}>
                        {successIcon}
                    <h2 style={headingStyle}>Success!</h2>
                    <p style={messageStyle}>{message}</p>
                    {renderBatchResults()}
                    <p style={timestampStyle}>Filed on {currentTime}</p>
                    <div style={buttonContainerStyle}>
                        <PrimaryButton 
                            text='File Another' 
                            onClick={back_to_start}
                            styles={{
                                root: { 
                                    minWidth: '140px', 
                                    height: '40px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    backgroundColor: '#5D3FD6',
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: '#4A2FC7'
                                },
                                rootPressed: {
                                    backgroundColor: '#3B24A8'
                                }
                            }}
                        />
                    </div>
                </div>
                </div>
            </div>
        );
    }

    if (link) {
        return (
            <div style={containerStyle}>
                <div style={modernStyles}>
                    <div style={{ textAlign: 'center' }}>
                        {successIcon}
                    <h2 style={headingStyle}>Email Filed Successfully!</h2>
                    <p style={messageStyle}>Your email has been successfully filed to Xplan.</p>
                    <p style={timestampStyle}>Filed on {currentTime}</p>
                    <div style={buttonContainerStyle}>
                        <PrimaryButton 
                            text={linkTitle || 'View in Xplan'} 
                            onClick={openInXplan}
                            styles={{
                                root: { 
                                    minWidth: '140px', 
                                    height: '40px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    backgroundColor: '#5D3FD6',
                                    border: 'none'
                                },
                                rootHovered: {
                                    backgroundColor: '#4A2FC7'
                                },
                                rootPressed: {
                                    backgroundColor: '#3B24A8'
                                }
                            }}
                        />
                        <DefaultButton 
                            text='File Another' 
                            onClick={back_to_start}
                            styles={{
                                root: { 
                                    minWidth: '140px', 
                                    height: '40px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    backgroundColor: '#ffffff',
                                    color: '#5D3FD6',
                                    border: '2px solid #5D3FD6'
                                },
                                rootHovered: {
                                    backgroundColor: '#f8f6fc',
                                    color: '#4A2FC7',
                                    border: '2px solid #4A2FC7'
                                },
                                rootPressed: {
                                    backgroundColor: '#ede8f7',
                                    color: '#3B24A8',
                                    border: '2px solid #3B24A8'
                                }
                            }}
                        />
                    </div>
                </div>
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            <div style={modernStyles}>
                <div style={{ textAlign: 'center' }}>
                    {successIcon}
                <h2 style={headingStyle}>Email Filed Successfully!</h2>
                <p style={messageStyle}>Your email has been successfully filed to Xplan.</p>
                <p style={timestampStyle}>Filed on {currentTime}</p>
                <div style={buttonContainerStyle}>
                    <PrimaryButton 
                        text='File Another' 
                        onClick={back_to_start}
                        styles={{
                            root: { 
                                minWidth: '140px', 
                                height: '40px',
                                fontSize: '14px',
                                fontWeight: 600,
                                borderRadius: '8px',
                                backgroundColor: '#5D3FD6',
                                border: 'none'
                            },
                            rootHovered: {
                                backgroundColor: '#4A2FC7'
                            },
                            rootPressed: {
                                backgroundColor: '#3B24A8'
                            }
                        }}
                    />
                </div>
            </div>
            </div>
        </div>
    );
}

export default SuccessMessage;
